<script lang="ts">
  import type {
    AssetField,
    ImageStoryblok,
    ImageCaptionStoryblok,
  } from "$types/components";
  import { storyblokEditable } from "@storyblok/svelte";
  import optimizeImage from "$lib/utils/optimizeImage";
  import { getAppContext } from "$lib/utils/contexts";

  export let blok: ImageStoryblok | undefined = undefined;

  export let loading: "eager" | "lazy" = "lazy";
  export let media: AssetField | undefined = undefined;
  export let caption: ImageCaptionStoryblok | null = null;
  export let isSVG = false;
  export let rounded = true;
  export const col: string | boolean | null = null;
  export let roundedMd: string | boolean | null = null;
  export let contain = false;
  export let aspect: string | null = null;
  export let aspectMd: string | null = null;

  export let lgSize: string | null = null;
  export let smSize: string | null = null;

  export let width: string | null = null;
  export let height: string | null = null;
  export let imageClasses = "";

  $: asset = media || blok?.object;

  let wrapper;

  $: imageWidth =
    !width && asset?.filename
      ? parseInt(asset.filename.split("/")[5].split("x")[0])
      : width;
  $: imageHeight =
    !height && asset?.filename
      ? parseInt(asset.filename.split("/")[5].split("x")[1])
      : height;

  function getAspect() {
    let aspectClass = "";
    if (aspect) {
      aspectClass += `aspect-${aspect}`;
      if (aspectMd) {
        aspectClass += ` aspect-md-${aspectMd}`;
      }
    }
    return aspectClass;
  }

  const getImageSrcSet = (url: string) => {
    const sizes = [900, 2160, 2880];
    const width = [600, 1440, 1920];

    const imageSrcSet = sizes
      .map((size, i) => `${url}/m/${size}x0/filters:quality(90) ${width[i]}w`)
      .join(", ");

    return imageSrcSet;
  };

  const isPostS6 = getAppContext("pageType") === "post-s6";
  const aspectClasses = getAspect();
</script>

{#if asset?.filename}
  <div
    bind:this={wrapper}
    class="media-wrapper {aspectClasses} {$$props.class ?? ''} max-h-full"
    class:rounded
    class:rounded-md={roundedMd || rounded}
    class:object-cover={aspect}
    class:fill-space={blok?.fillAvailableSpace}
    class:contain
    class:post-s6={isPostS6}
    use:storyblokEditable={asset}
  >
    <picture>
      <img
        draggable="false"
        src={optimizeImage(`${asset.filename}${!isSVG ? "/m/375x0" : ""}`)}
        data-src={optimizeImage(asset.filename)}
        alt={asset.alt}
        width={imageWidth}
        height={imageHeight}
        srcset={`${!isSVG ? getImageSrcSet(asset.filename) : ""}`}
        sizes={`(min-width: 768px) ${lgSize}vw, ${smSize}vw`}
        class="lazy-d {imageClasses}"
        class:w-[36px]={blok?.isIcon}
        class:h-[36px]={blok?.isIcon}
        {loading}
      />
    </picture>
  </div>
{/if}

{#if caption}
  <p class="caption type:body-medium">
    <span
      >{caption.primary}{(caption?.secondary?.length ?? 0) > 0 ? "," : ""}</span
    >
    <span>{caption.secondary}</span>
  </p>
{/if}

<style lang="scss">
  img:not([src]):not([data-srcset]) {
    visibility: hidden;
  }

  .loaded.entered {
    opacity: 1;
  }

  .media-wrapper {
    position: relative;
  }

  .rounded {
    overflow: hidden;
    border-radius: var(--border-radius-wrapper);
  }

  .rounded-md {
    @include breakpoint("small") {
      overflow: hidden;
      border-radius: var(--border-radius-wrapper);
    }
  }

  img {
    height: auto;
  }

  .object-cover {
    img {
      object-fit: cover;
      position: absolute;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
    }
  }

  .contain {
    img {
      object-fit: contain;
      position: absolute;
      top: 10px;
      left: 0;
      height: 100%;
      width: 100%;
    }
  }

  .caption {
    margin-top: fluid(12px, 10px);
  }

  .post-s6 picture {
    display: flex;
    justify-content: center;
  }

  .fill-space {
    width: 100%;

    picture {
      width: 100%;

      img {
        width: 100%;
        object-fit: cover;
      }
    }
  }
</style>
